<template>
  <div class="modal fade" id="companySubscribersModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl" style="max-width: 95%;">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Funcionários{{ company ? ' - ' + company.company_name : '' }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Info da empresa -->
          <div class="d-flex align-items-center bg-light-primary rounded p-3 mb-4" v-if="company">
            <div class="me-4">
              <i class="bi bi-building fs-2 text-primary"></i>
            </div>
            <div class="flex-grow-1">
              <div class="fw-bold fs-5 text-dark">{{ company.company_name }}</div>
              <div class="d-flex gap-4 mt-1">
                <span class="text-gray-700 fs-6"><strong>CNPJ:</strong> {{ company.formatted_cnpj }}</span>
                <span class="text-gray-700 fs-6"><strong>E-mail:</strong> {{ company.company_email }}</span>
              </div>
            </div>
          </div>

          <!-- Contrato para manutenção (apenas contratos ativos) -->
          <div class="row mb-4" v-if="company" :hidden="activeContracts.length === 1">
            <div class="col-md-12">
              <label class="form-label fw-bold">Selecione o Contrato</label>
              <DropdownUbbi
                v-if="activeContracts.length > 1"
                v-model="selectedContractId"
                :options="contractOptions"
                optionLabel="label"
                optionValue="id_contract"
                placeholder="Selecione o contrato"
                :filter="true"
                appendTo="self"
                @change="onContractChange"
              />
              <div v-else-if="activeContracts.length === 1" class="form-control form-control-solid py-2 bg-light">
                {{ contractOptions[0]?.label || (activeContracts[0]?.contract_number || 'Contrato ativo') }}
              </div>
              <p v-if="activeContracts.length === 0 && !contractsLoading" class="text-muted small mt-2 mb-0">
                Nenhum contrato ativo encontrado para esta empresa.
              </p>
              <p v-else-if="contractsLoading" class="text-muted small mt-2 mb-0">
                <span class="spinner-border spinner-border-sm me-1"></span> Carregando contratos...
              </p>
            </div>
          </div>

          <!-- Seção Novo cadastro + Importação -->
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div class="d-flex flex-wrap gap-2 align-items-center">
              <button type="button" class="btn btn-primary btn-sm" @click="showFormSection = !showFormSection">
                <i :class="showFormSection ? 'bi bi-chevron-up' : 'bi bi-plus'" class="me-1"></i>
                {{ showFormSection ? 'Ocultar formulário' : 'Novo funcionário' }}
              </button>
              <button type="button" class="btn btn-light-primary btn-sm" @click="downloadTemplate" :disabled="!selectedContractId"
                      title="Baixar planilha modelo para importação (selecione um contrato)">
                <i class="bi bi-download me-1"></i> Baixar modelo
              </button>
              <button type="button" class="btn btn-light-success btn-sm" @click="$refs.importFileInput?.click()" :disabled="!selectedContractId"
                      title="Importar funcionários via planilha CSV (selecione um contrato)">
                <i class="bi bi-upload me-1"></i> Importar planilha
              </button>
              <button type="button" class="btn btn-light-info btn-sm" @click="toggleImportResultsSection" :disabled="!selectedContractId"
                      :title="showImportResultsSection ? 'Ocultar resultado das importações' : 'Ver resultado das importações (selecione um contrato)'">
                <i :class="showImportResultsSection ? 'bi bi-chevron-up' : 'bi bi-list-check'" class="me-1"></i>
                {{ showImportResultsSection ? 'Ocultar resultado das importações' : 'Ver resultado das importações' }}
              </button>
              <input ref="importFileInput" type="file" accept=".csv" class="d-none" @change="onImportFileSelected" />
            </div>
          </div>

          <!-- Resultado das importações -->
          <div class="card card-bordered mb-4" v-show="showImportResultsSection">
            <div class="card-header py-3 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">Resultado das importações</h6>
              <button type="button" class="btn btn-sm btn-icon btn-light" @click="toggleImportResultsSection" title="Fechar">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <div class="card-body">
              <div v-if="importsLoading" class="text-center py-4">
                <span class="spinner-border spinner-border-sm"></span> Carregando...
              </div>
              <div v-else-if="!importsList.length" class="text-muted text-center py-4">
                Nenhuma importação encontrada.
              </div>
              <div v-else>
                <div class="table-responsive mb-3">
                  <table class="table table-sm table-hover">
                    <thead>
                      <tr class="fw-bold text-muted">
                        <th>Data</th>
                        <th>Status</th>
                        <th>Sucesso</th>
                        <th>Erros</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="imp in importsList" :key="imp.id">
                        <td>{{ formatDateTime(imp.created_at) }}</td>
                        <td>
                          <span :class="statusBadgeClass(imp.status)">{{ statusLabel(imp.status) }}</span>
                        </td>
                        <td>{{ imp.success_count ?? 0 }}</td>
                        <td>{{ imp.error_count ?? 0 }}</td>
                        <td>
                          <button type="button" class="btn btn-sm btn-light-primary" @click="toggleImportDetail(imp)">
                            {{ selectedImportDetail?.id === imp.id ? 'Ocultar detalhes' : 'Ver detalhes' }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <!-- Detalhe da importação selecionada -->
                <div v-if="selectedImportDetail" class="border rounded p-3 bg-light">
                  <div class="d-flex justify-content-between align-items-start mb-3">
                    <h6 class="mb-0">Importação {{ formatDateTime(selectedImportDetail.created_at) }}</h6>
                    <button type="button" class="btn btn-sm btn-icon btn-light" @click="selectedImportDetail = null">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                  <div class="mb-2" v-if="selectedImportDetail.parse_errors && selectedImportDetail.parse_errors.length">
                    <strong class="text-danger">Erros de validação (linhas):</strong>
                    <ul class="mb-0 small">
                      <li v-for="(err, i) in selectedImportDetail.parse_errors" :key="i">
                        Linha {{ err.linha ?? '-' }}: {{ (err.erros || []).join(', ') }}
                      </li>
                    </ul>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead>
                        <tr class="fw-bold text-muted">
                          <th>#</th>
                          <th>Nome</th>
                          <th>CPF</th>
                          <th>Status</th>
                          <th>Mensagem</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in (selectedImportDetail.rows || [])" :key="row.id">
                          <td>{{ row.row_index }}</td>
                          <td>{{ row.employee_name || '-' }}</td>
                          <td>{{ row.employee_cpf || '-' }}</td>
                          <td>
                            <span :class="row.status === 'success' ? 'badge badge-success' : 'badge badge-danger'">
                              {{ row.status === 'success' ? 'Sucesso' : 'Falha' }}
                            </span>
                          </td>
                          <td class="small">{{ row.error_message || '-' }}</td>
                        </tr>
                        <tr v-if="!selectedImportDetail.rows || !selectedImportDetail.rows.length">
                          <td colspan="5" class="text-center text-muted">Nenhum funcionário processado nesta importação.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card card-bordered mb-4" v-show="showFormSection">
            <div class="card-header py-3">
              <h6 class="mb-0">Novo cadastro</h6>
            </div>
            <div class="card-body" v-show="showFormSection">
              <form @submit.prevent="submit">
                <div class="row mb-2 align-items-end">
                  <div class="col-md-4">
                    <label class="form-label required">Plano</label>
                    <input type="text" class="form-control form-control-solid" disabled
                           :value="selectedContract?.plan?.plan_name || selectedContract?.template?.template_name || 'Selecione um contrato'" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Forma de Pagamento</label>
                    <input type="text" class="form-control form-control-solid" disabled value="Boleto Bancário" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label required">Data Início</label>
                    <Calendar class="form-control form-control-sm"
                              dateFormat="dd/mm/yy" v-model="form.start_date" />
                  </div>
                  <div class="col-md-1">
                    <label class="form-label d-block">&nbsp;</label>
                    <button type="button" class="btn btn-primary btn-icon" @click="addEmployee" title="Adicionar funcionário">
                      <i class="bi bi-plus"></i>
                    </button>
                  </div>
                </div>

                <div class="mb-2 mt-3">
                  <h6 class="text-muted mb-0 fw-bold">FUNCIONÁRIOS</h6>
                </div>

                <div v-for="(employee, empIndex) in form.employees" :key="'emp-' + empIndex" class="border rounded p-3 mb-3 bg-light-secondary">
                  <div class="row align-items-end mb-2">
                    <div class="col-md-3">
                      <label class="fs-6 fw-bold required mb-1">Funcionário</label>
                      <SearchPatient v-model="employee.user_name"
                                     :key="'emp-' + empIndex + '-' + uniqueKey"
                                     use-autocomplete
                                     @onClick="(p) => onClickSearchedEmployee(p, empIndex)" />
                    </div>
                    <div :class="employee.is_foreign ? 'col-md-6' : 'col-md-2'">
                      <PatientIdentityFields :form="employee" cpf-field="user_cpf" />
                    </div>
                    <div class="col-md-2">
                      <label class="fs-6 required fw-bold mb-1">Data Nascimento</label>
                      <input type="text" class="form-control form-control-solid"
                             v-mask="'##/##/####'" required
                             placeholder="DD/MM/AAAA" v-model="employee.user_dt_birth" />
                    </div>
                    <div class="col-md-2">
                      <label class="fs-6 required fw-bold mb-1">Telefone</label>
                      <input type="text" class="form-control form-control-solid"
                             v-mask="['(##) ####-####', '(##) #####-####']"
                             placeholder="(00) 00000-0000" v-model="employee.user_phone" />
                    </div>
                    <div class="col-md-1">
                      <label class="fs-6 fw-bold mb-1 d-block">&nbsp;</label>
                      <button class="btn btn-danger btn-sm" type="button" @click="removeEmployee(empIndex)" title="Remover funcionário">
                        <i class="bi bi-trash2"></i>
                      </button>
                    </div>
                  </div>
                  <!-- Dependentes do funcionário -->
                  <div class="mt-2 ps-3 border-start border-primary border-3">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span class="fs-7 fw-bold text-muted">Dependentes</span>
                    </div>
                    <div v-for="(dep, depIndex) in (employee.dependents || [])" :key="'dep-' + empIndex + '-' + depIndex" class="row align-items-end mb-2 g-2">
                      <div class="col-md-3">
                        <label class="fs-7 mb-1">Paciente</label>
                        <SearchPatient v-model="dep.user_name"
                                       :key="'dep-' + empIndex + '-' + depIndex + '-' + uniqueKey"
                                       use-autocomplete
                                       @onClick="(p) => onClickSearchedDependent(p, empIndex, depIndex)" />
                      </div>
                      <div :class="dep.is_foreign ? 'col-md-6' : 'col-md-2'">
                        <PatientIdentityFields :form="dep" cpf-field="user_cpf" />
                      </div>
                      <div class="col-md-2">
                        <label class="fs-7 mb-1">Nascimento</label>
                        <input type="text" class="form-control form-control-solid"
                               v-mask="'##/##/####'"
                               placeholder="DD/MM/AAAA" v-model="dep.user_dt_birth" />
                      </div>
                      <div class="col-md-2">
                        <label class="fs-7 mb-1">Telefone</label>
                        <input type="text" class="form-control form-control-solid"
                               v-mask="['(##) ####-####', '(##) #####-####']"
                               v-model="dep.user_phone" />
                      </div>
                      <div class="col-md-2">
                        <label class="fs-7 mb-1">Relacionamento</label>
                        <input type="text" class="form-control form-control-solid"
                               placeholder="Ex: Cônjuge, Filho(a)" v-model="dep.dep_relationship" />
                      </div>
                      <div class="col-md-1">
                        <button class="btn btn-sm btn-light-danger" type="button" @click="removeDependent(empIndex, depIndex)" title="Remover dependente">
                          <i class="bi bi-trash2"></i>
                        </button>
                      </div>
                    </div>
                    <div class="mt-2">
                      <button type="button" class="btn btn-sm btn-light-primary" @click="addDependent(empIndex)">
                        <i class="bi bi-person-plus me-1"></i> Adicionar dependente
                      </button>
                    </div>
                  </div>
                </div>

                <div class="mt-3">
                  <button type="submit" class="btn btn-primary">Salvar funcionários</button>
                </div>
              </form>
            </div>
          </div>

          <!-- Listagem de funcionários -->
          <hr class="my-4">
          <h6 class="text-muted mb-2 fw-bold">FUNCIONÁRIOS CADASTRADOS</h6>
          <div class="row mb-3">
            <div class="col-md-10">
              <input type="search" v-model="search" @input="loadSubscribers"
                     class="form-control form-control-solid"
                     placeholder="Buscar por nome ou CPF..." />
            </div>
            <div class="col-md-2">
              <select v-model="statusFilter" @change="loadSubscribers" class="form-control form-control-solid">
                <option value="">Todos os status</option>
                <option value="active">Ativo</option>
                <option value="suspended">Suspenso</option>
                <option value="cancelled">Cancelado</option>
                <option value="pending">Pendente</option>
              </select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover table-row-bordered">
              <thead>
              <tr class="fw-bold fs-6 text-muted">
                <th style="width: 36px;"></th>
                <th>Nome</th>
                <th>CPF</th>
                <th>Telefone</th>
                <th>Dependentes</th>
                <th>Status</th>
                <th class="text-center">Ações</th>
              </tr>
              </thead>
              <tbody>
              <template v-for="subscriber in subscribers" :key="subscriber.id_subscriber">
                <tr>
                  <td class="align-middle">
                    <button type="button" class="btn btn-sm btn-icon btn-light"
                            @click="toggleExpand(subscriber.id_subscriber)"
                            :title="expandedRow === subscriber.id_subscriber ? 'Ocultar dependentes' : 'Ver dependentes'">
                      <i :class="expandedRow === subscriber.id_subscriber ? 'bi bi-chevron-down' : 'bi bi-chevron-right'"></i>
                    </button>
                  </td>
                  <td>{{ subscriber.user?.user_name }}</td>
                  <td>{{ subscriber.user?.document_number || subscriber.user?.user_cpf }}</td>
                  <td>{{ subscriber.user?.user_phone }}</td>
                  <td>
                    <span v-if="subscriber.dependents && subscriber.dependents.filter(d => !d.dep_main).length > 0">
                      {{ subscriber.dependents.filter(d => !d.dep_main).length }} dependente(s)
                    </span>
                    <span v-else class="text-muted">Nenhum</span>
                  </td>
                  <td>{{ subscriber.subs_status_dsc }}</td>
                  <td class="text-center">
                    <button v-if="subscriber.subs_status !== 'cancelled'"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger border border-gray-400"
                            type="button" @click="cancelSubscriber(subscriber)" title="Cancelar assinante">
                      <i class="bi bi-x-circle"></i>
                    </button>
                    <button v-else
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-success border border-gray-400"
                            type="button" @click="reactivateSubscriber(subscriber)" title="Reativar assinatura">
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="expandedRow === subscriber.id_subscriber" class="bg-light">
                  <td colspan="7" class="p-0">
                    <div class="p-3">
                      <table class="table table-sm table-bordered mb-0">
                        <thead>
                        <tr class="fw-bold fs-7 text-muted">
                          <th>Nome</th>
                          <th>CPF</th>
                          <th>Nascimento</th>
                          <th>Relacionamento</th>
                          <th>Status</th>
                          <th v-if="subscriber.subs_status !== 'cancelled'" class="text-end" style="width: 120px;">Ações</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="dep in (subscriber.dependents || []).filter(d => !d.dep_main)" :key="dep.id_dependent">
                          <td>{{ dep.patient?.patient_name }}</td>
                          <td>{{ dep.patient?.document_number || dep.patient?.patient_cpf }}</td>
                          <td>{{ formatDateBR(dep.patient?.patient_birthdate) }}</td>
                          <td>
                            <template v-if="isEditingDependent(subscriber, dep)">
                              <div class="d-flex align-items-center gap-1">
                                <input type="text" class="form-control form-control-sm"
                                       v-model="editDepRelationship"
                                       placeholder="Ex: Cônjuge, Filho(a)"
                                       @keydown.enter="saveDependentRelationship(subscriber, dep)" />
                                <button type="button" class="btn btn-sm btn-success btn-icon"
                                        @click="saveDependentRelationship(subscriber, dep)" title="Salvar">
                                  <i class="bi bi-check"></i>
                                </button>
                                <button type="button" class="btn btn-sm btn-light btn-icon"
                                        @click="cancelEditDependent" title="Cancelar">
                                  <i class="bi bi-x"></i>
                                </button>
                              </div>
                            </template>
                            <template v-else>
                              {{ dep.dep_relationship || '-' }}
                            </template>
                          </td>
                          <td>
                            <span :class="dep.dep_active !== false ? 'badge badge-success' : 'badge badge-secondary'">
                              {{ dep.dep_active !== false ? 'Ativo' : 'Cancelado' }}
                            </span>
                          </td>
                          <td v-if="subscriber.subs_status !== 'cancelled'" class="text-end">
                            <button v-if="!isEditingDependent(subscriber, dep)"
                                    type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400 me-1"
                                    @click="openEditDependent(subscriber, dep)"
                                    title="Editar relacionamento">
                              <i class="bi bi-pen"></i>
                            </button>
                            <button v-if="dep.dep_active !== false"
                                    type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger border border-gray-400 me-1"
                                    @click="cancelDependent(subscriber, dep)"
                                    title="Cancelar dependente">
                              <i class="bi bi-x-circle"></i>
                            </button>
                            <button v-else
                                    type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-success border border-gray-400"
                                    @click="reactivateDependent(subscriber, dep)"
                                    title="Reativar dependente">
                              <i class="bi bi-arrow-counterclockwise"></i>
                            </button>
                          </td>
                        </tr>
                        <tr v-if="!(subscriber.dependents || []).filter(d => !d.dep_main).length && subscriber.subs_status !== 'cancelled'">
                          <td colspan="6" class="text-center text-muted">Nenhum dependente</td>
                        </tr>
                        <tr v-else-if="!(subscriber.dependents || []).filter(d => !d.dep_main).length">
                          <td colspan="5" class="text-center text-muted">Nenhum dependente</td>
                        </tr>
                        </tbody>
                      </table>
                      <!-- Formulário adicionar dependente (apenas assinante ativo) -->
                      <div v-if="subscriber.subs_status !== 'cancelled'" class="mt-3 border rounded p-3 bg-white">
                        <h6 class="fw-bold text-muted mb-2">Adicionar dependente</h6>
                        <div class="row g-2 align-items-end">
                          <div class="col-md-3">
                            <label class="form-label small mb-0">Paciente</label>
                            <SearchPatient :modelValue="(newDependentBySubscriber[subscriber.id_subscriber] && newDependentBySubscriber[subscriber.id_subscriber].user_name) || ''"
                                           @update:modelValue="setNewDependentField(subscriber.id_subscriber, 'user_name', $event)"
                                           :key="'add-dep-' + subscriber.id_subscriber + '-' + uniqueKey"
                                           use-autocomplete
                                           @onClick="(p) => onSearchedPatientForDependent(p, subscriber)" />
                          </div>
                          <div :class="newDependentForm(subscriber.id_subscriber).is_foreign ? 'col-md-6' : 'col-md-2'">
                            <PatientIdentityFields :form="newDependentForm(subscriber.id_subscriber)" cpf-field="user_cpf" />
                          </div>
                          <div class="col-md-2">
                            <label class="form-label small mb-0">Nascimento</label>
                            <input type="text" class="form-control form-control-sm"
                                   v-mask="'##/##/####'"
                                   placeholder="DD/MM/AAAA"
                                   :value="newDependentBySubscriber[subscriber.id_subscriber]?.user_birthdate"
                                   @input="setNewDependentField(subscriber.id_subscriber, 'user_birthdate', $event.target.value)" />
                          </div>
                          <div class="col-md-2">
                            <label class="form-label small mb-0">Telefone</label>
                            <input type="text" class="form-control form-control-sm"
                                   v-mask="['(##) ####-####', '(##) #####-####']"
                                   placeholder="(00) 00000-0000"
                                   :value="newDependentBySubscriber[subscriber.id_subscriber]?.user_phone"
                                   @input="setNewDependentField(subscriber.id_subscriber, 'user_phone', $event.target.value)" />
                          </div>
                          <div class="col-md-2">
                            <label class="form-label small mb-0">Relacionamento</label>
                            <input type="text" class="form-control form-control-sm"
                                   placeholder="Ex: Cônjuge, Filho(a)"
                                   :value="newDependentBySubscriber[subscriber.id_subscriber]?.dep_relationship"
                                   @input="setNewDependentField(subscriber.id_subscriber, 'dep_relationship', $event.target.value)" />
                          </div>
                          <div class="col-md-1">
                            <label class="form-label small mb-0 d-block">&nbsp;</label>
                            <button type="button" class="btn btn-primary btn-sm w-100"
                                    :disabled="addingDependentSubscriberId === subscriber.id_subscriber || !isNewDependentValid(subscriber.id_subscriber)"
                                    @click="addLinkedDependent(subscriber)">
                              <span v-if="addingDependentSubscriberId === subscriber.id_subscriber" class="spinner-border spinner-border-sm me-1"></span>
                              <i v-else class="bi bi-person-plus me-1"></i>
                              Adicionar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="subscribers.length === 0">
                <td colspan="7" class="text-center text-muted py-5">Nenhum funcionário encontrado.</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Fechar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { patientIdentity } from '../../../helpers/patientIdentity';
import http from "@/helpers/http";
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import Download from "@/helpers/download";
import SearchPatient from "@/components/SearchPatient.vue";
import DropdownUbbi from "@/components/DropdownUbbi.vue";
import Calendar from "primevue/calendar";
import { mask } from "vue-the-mask";
import { CompanyService } from "@/service/companyService";

export default {
  name: "CompanySubscribersModal",
  props: {
    company: { type: Object, default: null },
    expandFormOnOpen: { type: Boolean, default: false },
  },
  emits: ['onClose', 'subscriptionSaved', 'opened'],
  components: { SearchPatient, DropdownUbbi, Calendar },
  directives: { mask },
  data() {
    return {
      subscribers: [],
      search: '',
      statusFilter: '',
      expandedRow: null,
      showFormSection: false,
      showImportResultsSection: false,
      importsList: [],
      importsLoading: false,
      selectedImportDetail: null,
      uniqueKey: Date.now().toString(),
      newDependentBySubscriber: {},
      addingDependentSubscriberId: null,
      editingDependent: null,
      editDepRelationship: '',
      form: {
        start_date: this.getToday(),
        employees: [{ ...this.newEmployeeRow() }],
      },
      companyService: CompanyService.new(),
      activeContracts: [],
      selectedContractId: null,
      contractsLoading: false,
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    /** Opções do dropdown: apenas contratos ativos, com label legível */
    contractOptions() {
      return this.activeContracts.map((c) => ({
        id_contract: c.id_contract,
        label: `${c.contract_number || 'Contrato #' + c.id_contract}${c.template?.template_name ? ' - ' + c.template.template_name : ''}`,
      }));
    },
    /** Contrato atualmente selecionado (para obter id_plan ao adicionar funcionário) */
    selectedContract() {
      if (!this.selectedContractId) return null;
      return this.activeContracts.find((c) => c.id_contract === this.selectedContractId) || null;
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadActiveContracts();
          this.resetForm();
          if (this.expandFormOnOpen) {
            this.showFormSection = true;
            this.$emit('opened');
          }
        } else {
          this.subscribers = [];
          this.activeContracts = [];
          this.selectedContractId = null;
        }
      }
    }
  },
  methods: {
    getToday() {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    },
    resetForm() {
      this.uniqueKey = Date.now().toString();
      this.form = {
        start_date: this.getToday(),
        employees: [this.newEmployeeRow()],
      };
    },
    newEmployeeRow() {
      return {
        user_name: '',
        user_cpf: '',
        user_dt_birth: '',
        user_phone: '',
        dependents: [],
      };
    },
    newDependentRow() {
      return {
        user_name: '',
        user_cpf: '',
        user_dt_birth: '',
        user_phone: '',
        dep_relationship: '',
      };
    },
    addEmployee() {
      this.form.employees = [...this.form.employees, this.newEmployeeRow()];
    },
    addDependent(empIndex) {
      const emp = this.form.employees[empIndex];
      if (!emp.dependents) emp.dependents = [];
      this.form.employees = this.form.employees.map((e, i) =>
        i === empIndex ? { ...e, dependents: [...e.dependents, this.newDependentRow()] } : e
      );
    },
    removeEmployee(index) {
      Swal.fire({
        title: 'Remover funcionário',
        text: 'Tem certeza que deseja remover este funcionário e seus dependentes do cadastro?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          this.form.employees.splice(index, 1);
        }
      });
    },
    removeDependent(empIndex, depIndex) {
      this.form.employees[empIndex].dependents.splice(depIndex, 1);
      this.form.employees = [...this.form.employees];
    },
    toggleExpand(id) {
      this.expandedRow = this.expandedRow === id ? null : id;
    },
    onClickSearchedEmployee(patient, index) {
      const employee = this.form.employees[index];
      if (patient.patient_name) employee.user_name = patient.patient_name;
      Object.assign(employee, patientIdentity(patient));
      employee.user_cpf = patient.patient_cpf || null;
      if (patient.patient_phone) employee.user_phone = patient.patient_phone;
      if (patient.patient_birthdate_formatted) employee.user_dt_birth = patient.patient_birthdate_formatted;
    },
    onClickSearchedDependent(patient, empIndex, depIndex) {
      const dep = this.form.employees[empIndex].dependents[depIndex];
      if (patient.patient_name) dep.user_name = patient.patient_name;
      Object.assign(dep, patientIdentity(patient));
      dep.user_cpf = patient.patient_cpf || null;
      if (patient.patient_phone) dep.user_phone = patient.patient_phone;
      if (patient.patient_birthdate_formatted) dep.user_dt_birth = patient.patient_birthdate_formatted;
    },
    newDependentForm(id) {
      if (!this.newDependentBySubscriber[id]) this.newDependentBySubscriber[id] = {};
      return this.newDependentBySubscriber[id];
    },
    setNewDependentField(subscriberId, field, value) {
      if (!this.newDependentBySubscriber[subscriberId]) {
        this.newDependentBySubscriber = { ...this.newDependentBySubscriber, [subscriberId]: {} };
      }
      this.newDependentBySubscriber[subscriberId] = { ...this.newDependentBySubscriber[subscriberId], [field]: value };
    },
    isNewDependentValid(subscriberId) {
      const d = this.newDependentBySubscriber[subscriberId];
      if (!d) return false;
      const cpf = (d.user_cpf || '').replace(/\D/g, '');
      return (d.user_name && d.user_name.trim()) && (d.document_number || cpf.length === 11);
    },
    onSearchedPatientForDependent(patient, subscriber) {
      const id = subscriber.id_subscriber;
      const current = this.newDependentBySubscriber[id] || {};
      const d = { ...current };
      if (patient.patient_name) d.user_name = patient.patient_name;
      Object.assign(d, patientIdentity(patient));
      d.user_cpf = patient.patient_cpf || null;
      if (patient.patient_phone) d.user_phone = patient.patient_phone || '';
      if (patient.patient_birthdate_formatted) d.user_birthdate = patient.patient_birthdate_formatted;
      else if (patient.patient_birthdate) d.user_birthdate = patient.patient_birthdate;
      this.newDependentBySubscriber = { ...this.newDependentBySubscriber, [id]: d };
      this.uniqueKey = Date.now().toString();
    },
    birthdateToYmd(value) {
      if (!value || typeof value !== 'string') return value;
      const parts = value.trim().split(/[/-]/);
      if (parts.length !== 3) return value;
      const [a, b, c] = parts;
      if (a.length === 4) return value;
      return `${c}-${b}-${a}`;
    },
    addLinkedDependent(subscriber) {
      const id = subscriber.id_subscriber;
      const d = this.newDependentBySubscriber[id];
      if (!d || !this.isNewDependentValid(id)) return;
      this.addingDependentSubscriberId = id;
      const payload = {
        user_name: (d.user_name || '').trim(),
        ...patientIdentity(d),
        user_cpf: (d.user_cpf || '').replace(/\D/g, ''),
        user_phone: (d.user_phone || '').replace(/\D/g, '') || null,
        user_birthdate: this.birthdateToYmd(d.user_birthdate) || d.user_birthdate,
        dep_relationship: (d.dep_relationship || '').trim() || null,
      };
      http.post(`/places/${this.place.id_place}/plans/companies/${this.company.id_company}/subscribers/${id}/dependents`, payload)
        .then((res) => {
          const list = res.data?.data ?? res.data;
          const dependents = Array.isArray(list) ? list : (subscriber.dependents || []);
          const idx = this.subscribers.findIndex((s) => s.id_subscriber === id);
          if (idx !== -1) this.subscribers[idx].dependents = dependents;
          const next = { ...this.newDependentBySubscriber };
          delete next[id];
          this.newDependentBySubscriber = next;
          this.uniqueKey = Date.now().toString();
          Alert.success('Dependente adicionado.');
        })
        .catch((err) => {
          const msg = err.response?.data?.message || 'Erro ao adicionar dependente.';
          Alert.error(typeof msg === 'string' ? msg : 'Erro ao adicionar dependente.');
        })
        .finally(() => {
          this.addingDependentSubscriberId = null;
        });
    },
    isEditingDependent(subscriber, dep) {
      return this.editingDependent
        && this.editingDependent.subscriberId === subscriber.id_subscriber
        && this.editingDependent.id_dependent === dep.id_dependent;
    },
    openEditDependent(subscriber, dep) {
      this.editingDependent = { subscriberId: subscriber.id_subscriber, id_dependent: dep.id_dependent };
      this.editDepRelationship = dep.dep_relationship || '';
    },
    cancelEditDependent() {
      this.editingDependent = null;
      this.editDepRelationship = '';
    },
    saveDependentRelationship(subscriber, dep) {
      const id = subscriber.id_subscriber;
      const relationship = (this.editDepRelationship || '').trim();
      Loader.show('Salvando...');
      http.patch(`/places/${this.place.id_place}/plans/companies/${this.company.id_company}/subscribers/${id}/dependents/${dep.id_dependent}`, { dep_relationship: relationship })
        .then(() => {
          Loader.close();
          Alert.success('Relacionamento atualizado.');
          this.cancelEditDependent();
          this.loadSubscribers();
        })
        .catch((err) => {
          Loader.close();
          const msg = err.response?.data?.message || 'Erro ao atualizar relacionamento.';
          Alert.error(typeof msg === 'string' ? msg : msg);
        });
    },
    cancelDependent(subscriber, dep) {
      if (dep.dep_main || dep.dep_active === false) return;
      Swal.fire({
        title: 'Cancelar dependente',
        text: `Cancelar "${dep.patient?.patient_name || 'este dependente'}" no plano? O dependente não será excluído e poderá ser reativado depois.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar',
        cancelButtonText: 'Voltar',
        confirmButtonColor: '#d33',
      }).then((result) => {
        if (!result.isConfirmed) return;
        const id = subscriber.id_subscriber;
        Loader.show('Cancelando dependente...');
        http.patch(`/places/${this.place.id_place}/plans/companies/${this.company.id_company}/subscribers/${id}/dependents/${dep.id_dependent}`, { dep_active: false })
          .then(() => {
            Loader.close();
            Alert.success('Dependente cancelado.');
            this.loadSubscribers();
          })
          .catch((err) => {
            Loader.close();
            const msg = err.response?.data?.message || 'Erro ao cancelar dependente.';
            Alert.error(typeof msg === 'string' ? msg : 'Erro ao cancelar dependente.');
          });
      });
    },
    reactivateDependent(subscriber, dep) {
      if (dep.dep_main || dep.dep_active !== false) return;
      Swal.fire({
        title: 'Reativar dependente',
        text: `Reativar "${dep.patient?.patient_name || 'este dependente'}" no plano?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reativar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0d6efd',
      }).then((result) => {
        if (!result.isConfirmed) return;
        const id = subscriber.id_subscriber;
        Loader.show('Reativando dependente...');
        http.patch(`/places/${this.place.id_place}/plans/companies/${this.company.id_company}/subscribers/${id}/dependents/${dep.id_dependent}`, { dep_active: true })
          .then(() => {
            Loader.close();
            Alert.success('Dependente reativado com sucesso.');
            this.loadSubscribers();
          })
          .catch((err) => {
            Loader.close();
            const msg = err.response?.data?.message || 'Erro ao reativar dependente.';
            Alert.error(typeof msg === 'string' ? msg : 'Erro ao reativar dependente.');
          });
      });
    },
    loadActiveContracts() {
      if (!this.company) return;

      this.contractsLoading = true;
      this.companyService.contracts(this.place.id_place, this.company.id_company, { per_page: 100 })
        .then((response) => {
          const list = response.data?.data ?? response.data ?? [];
          const all = Array.isArray(list) ? list : [];
          this.activeContracts = all.filter((c) => c.contract_status === 'active');
          this.selectedContractId = this.activeContracts[0]?.id_contract ?? null;
          this.loadSubscribers();
        })
        .catch((err) => {
          console.error('Erro ao carregar contratos:', err);
          this.activeContracts = [];
          this.selectedContractId = null;
        })
        .finally(() => {
          this.contractsLoading = false;
        });
    },
    onContractChange() {
      this.loadSubscribers();
      if (this.showImportResultsSection) {
        this.loadImportsList();
      }
      this.selectedImportDetail = null;
    },
    loadSubscribers() {
      if (!this.company) return;

      const params = {
        search: this.search || null,
        status: this.statusFilter || null,
      };
      if (this.selectedContractId) {
        params.id_contract = this.selectedContractId;
      }

      this.companyService.subscribers(this.place.id_place, this.company.id_company, params)
        .then((response) => {
          this.subscribers = response.data?.data || response.data || [];
        })
        .catch(() => {
          this.subscribers = [];
        });
    },
    downloadTemplate() {
      if (!this.selectedContractId) {
        Alert.error('Selecione um contrato para baixar o modelo.');
        return;
      }
      Loader.show('Baixando modelo...');
      const params = { id_company: this.company?.id_company, id_contract: this.selectedContractId };
      this.companyService.downloadEmployeesTemplate(this.place.id_place, params)
        .then((response) => {
          Download.downloadFile(response.data, 'modelo_funcionarios_empresa.csv', 'text/csv');
          Loader.close();
        })
        .catch((error) => {
          Loader.close();
          const msg = error.response?.data?.message || 'Erro ao baixar modelo.';
          Alert.error(typeof msg === 'string' ? msg : 'Erro ao baixar modelo.');
        });
    },
    onImportFileSelected(event) {
      const file = event.target.files?.[0];
      if (!file || !this.company) return;
      if (!this.selectedContractId) {
        Alert.error('Selecione um contrato para importar a planilha.');
        event.target.value = '';
        return;
      }
      if (!file.name.toLowerCase().endsWith('.csv')) {
        Alert.error('Selecione apenas arquivos CSV.');
        event.target.value = '';
        return;
      }

      const startDate = this.form.start_date
        ? (this.form.start_date instanceof Date
          ? this.form.start_date.toISOString().slice(0, 10)
          : this.form.start_date)
        : null;

      Loader.show('Enviando planilha...');
      this.companyService.importEmployees(this.place.id_place, this.company.id_company, file, startDate, this.selectedContractId)
        .then((response) => {
          Loader.close();
          const data = response.data?.data ?? response.data ?? {};
          Alert.success(response.data?.message || 'Importação enfileirada. Acompanhe o status em "Ver resultado das importações".');
          this.loadSubscribers();
          this.$emit('subscriptionSaved');
          this.showImportResultsSection = true;
          this.loadImportsList();
        })
        .catch((error) => {
          Loader.close();
          const msg = error.response?.data?.message || error.response?.data?.errors?.file?.[0] || 'Erro ao enviar planilha.';
          Alert.error(typeof msg === 'string' ? msg : 'Erro ao enviar planilha.');
        })
        .finally(() => {
          event.target.value = '';
        });
    },
    toggleImportResultsSection() {
      if (!this.showImportResultsSection && !this.selectedContractId) {
        Alert.error('Selecione um contrato para ver o resultado das importações.');
        return;
      }
      this.showImportResultsSection = !this.showImportResultsSection;
      if (this.showImportResultsSection) {
        this.selectedImportDetail = null;
        this.loadImportsList();
      }
    },
    loadImportsList() {
      if (!this.company) return;
      this.importsLoading = true;
      const params = this.selectedContractId ? { id_contract: this.selectedContractId } : {};
      this.companyService.listEmployeesImports(this.place.id_place, this.company.id_company, params)
        .then((response) => {
          this.importsList = response.data?.data ?? response.data ?? [];
        })
        .catch(() => { this.importsList = []; })
        .finally(() => { this.importsLoading = false; });
    },
    toggleImportDetail(imp) {
      if (this.selectedImportDetail?.id === imp.id) {
        this.selectedImportDetail = null;
        return;
      }
      this.loadImportDetail(imp.id);
    },
    loadImportDetail(importId) {
      if (!this.company) return;
      const params = this.selectedContractId ? { id_contract: this.selectedContractId } : {};
      this.companyService.showEmployeesImport(this.place.id_place, this.company.id_company, importId, params)
        .then((response) => {
          this.selectedImportDetail = response.data?.data ?? response.data;
        })
        .catch(() => { this.selectedImportDetail = null; });
    },
    formatDateTime(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    formatDateBR(value) {
      if (!value) return '-';
      const str = String(value).trim();
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      const d = new Date(str);
      if (Number.isNaN(d.getTime())) return str;
      return d.toLocaleDateString('pt-BR');
    },
    statusLabel(status) {
      const map = { pending: 'Pendente', processing: 'Processando', completed: 'Concluído', failed: 'Falhou' };
      return map[status] || status;
    },
    statusBadgeClass(status) {
      const map = {
        pending: 'badge badge-warning',
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
      };
      return map[status] || 'badge badge-secondary';
    },
    submit() {
      if (!this.company) return;

      const paymentDay = this.company.payment_day || 10;
      if (paymentDay < 1 || paymentDay > 28) {
        Alert.error('Dia de pagamento da empresa deve ser entre 1 e 28. Configure na edição da empresa.');
        return;
      }

      const validEmployees = this.form.employees.filter(
        emp => emp.document_number || (emp.user_cpf && String(emp.user_cpf).replace(/\D/g, '').length >= 11)
      );
      if (validEmployees.length === 0) {
        Alert.error('Informe ao menos um funcionário com documento válido.');
        return;
      }

      Loader.show('Salvando...');

      const idPlan = this.selectedContract?.id_plan ?? this.selectedContract?.plan?.id_plan ?? this.company.id_plan;
      const payload = {
        id_plan: idPlan,
        id_company: this.company.id_company,
        subscriber_type: 'company',
        plan_payment_method: 'bank_slip',
        billing_mode: 'monthly',
        payment_day: paymentDay,
        start_date: this.form.start_date || null,
        ...(this.selectedContractId ? { id_contract: this.selectedContractId } : {}),
        employees: validEmployees.map(emp => ({
          user_name: emp.user_name,
          ...patientIdentity(emp),
          user_cpf: emp.user_cpf,
          user_dt_birth: emp.user_dt_birth,
          user_phone: emp.user_phone,
          dep_relationship: 'Funcionario',
          dependents: (emp.dependents || []).filter(
            d => d.document_number || (d.user_cpf && String(d.user_cpf).replace(/\D/g, '').length >= 11)
          ).map(d => ({
            user_name: d.user_name,
            ...patientIdentity(d),
          user_cpf: d.user_cpf,
            user_dt_birth: d.user_dt_birth,
            user_phone: d.user_phone,
            dep_relationship: d.dep_relationship || null,
          })),
        })),
      };

      http.post(`/places/${this.place.id_place}/subscribers`, payload)
        .then(() => {
          Loader.close();
          Alert.success('Funcionários cadastrados com sucesso.');
          this.$emit('subscriptionSaved');
          this.resetForm();
          this.loadSubscribers();
        })
        .catch((error) => {
          Loader.close();
          const errorMessage = error.response?.data?.message || 
                              (typeof error.response?.data === 'string' ? error.response.data : null) ||
                              error.response?.data ||
                              'Erro ao salvar.';
          Alert.error(errorMessage);
        });
    },
    cancelSubscriber(subscriber) {
      Swal.fire({
        title: 'Cancelar assinante',
        text: `Tem certeza que deseja cancelar a assinatura de "${subscriber.user?.user_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Cancelando...');
          this.companyService.cancelSubscriber(this.place.id_place, this.company.id_company, subscriber.id_subscriber)
            .then(() => {
              Loader.close();
              Alert.success('Assinante cancelado com sucesso.');
              this.loadSubscribers();
            })
            .catch((error) => {
              Loader.close();
              const errorMessage = error.response?.data?.message || 
                                  (typeof error.response?.data === 'string' ? error.response.data : null) ||
                                  error.response?.data ||
                                  'Erro ao cancelar.';
              Alert.error(errorMessage);
            });
        }
      });
    },
    reactivateSubscriber(subscriber) {
      Swal.fire({
        title: 'Reativar assinatura',
        text: `Reativar a assinatura de "${subscriber.user?.user_name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reativar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0d6efd',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Reativando...');
          this.companyService.reactivateSubscriber(this.place.id_place, this.company.id_company, subscriber.id_subscriber)
            .then(() => {
              Loader.close();
              Alert.success('Assinatura reativada com sucesso.');
              this.loadSubscribers();
            })
            .catch((error) => {
              Loader.close();
              const errorMessage = error.response?.data?.message ||
                                  (typeof error.response?.data === 'string' ? error.response.data : null) ||
                                  error.response?.data ||
                                  'Erro ao reativar.';
              Alert.error(errorMessage);
            });
        }
      });
    },
    closeModal() {
      Modal.toggle('#companySubscribersModal');
      this.$emit('onClose');
      this.search = '';
      this.statusFilter = '';
      this.expandedRow = null;
      this.selectedContractId = null;
      this.activeContracts = [];
      this.cancelEditDependent();
      this.resetForm();
    },
  }
}
</script>
